import React, { useState, useEffect, useCallback } from 'react';
import { auth, db } from '../../firebase';
import {
    Plus, Trash2, Eye, EyeOff,
    Printer, Save, Download, Upload, Settings,
    BarChart, Copy, LogOut
} from '../Icons';

// Reusable Button component with better UX
interface ButtonProps {
    onClick?: () => void;
    children: React.ReactNode;
    variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'ghost';
    size?: 'sm' | 'md' | 'lg';
    disabled?: boolean;
    loading?: boolean;
    title?: string;
    className?: string;
    type?: 'button' | 'submit';
}

const Button: React.FC<ButtonProps> = ({
    onClick, children, variant = 'secondary', size = 'md',
    disabled = false, loading = false, title, className = '', type = 'button'
}) => {
    const baseClasses = 'inline-flex items-center justify-center gap-1.5 font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed';

    const variants = {
        primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 shadow-sm hover:shadow',
        secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-400 border border-gray-300',
        danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
        success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
        ghost: 'text-gray-600 hover:bg-gray-100 focus:ring-gray-400'
    };

    const sizes = {
        sm: 'px-2 py-1 text-xs',
        md: 'px-3 py-1.5 text-sm',
        lg: 'px-4 py-2 text-base'
    };

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled || loading}
            title={title}
            className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
        >
            {loading ? (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
            ) : null}
            {children}
        </button>
    );
};
import {
    ProposalSection as IProposalSection, ProposalClientInfo, ProposalCompanyInfo,
    ProposalPaymentTerms, ProposalDiscount, ProposalVat,
    ProposalTerms, ProposalColors, SavedProposal, ProposalStatus, SavedClient, Currency
} from '../../types';
import { ProposalSection } from './ProposalSection';
import { ProposalSettings } from './ProposalSettings';
import firebase from 'firebase/compat/app';
import * as firestoreService from '../../services/firestoreService';
import { toast } from 'sonner';
import { APP_CONFIG } from '../../config';

interface CommercialProposalProps {
    user: firebase.User;
}

export function CommercialProposal({ user }: CommercialProposalProps) {
    // STATES
    const [showCosts, setShowCosts] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [showTemplates, setShowTemplates] = useState(false);
    const [language, setLanguage] = useState<'lt' | 'en'>('lt');

    // ADMIN PANEL STATE
    const [adminPanel, setAdminPanel] = useState({ newUserEmail: '', allowedUsers: [] as any[] });
    const isAdmin = user.email?.toLowerCase() === APP_CONFIG.ADMIN_EMAIL.toLowerCase();

    const [sections, setSections] = useState<IProposalSection[]>([{
        id: 1,
        title: 'I. Paslaugų skyrius',
        services: [{
            id: 1,
            name: 'Paslauga 1',
            unitPrice: 1000,
            quantity: 1,
            expanded: false,
            details: [],
            costs: [],
            isRecurring: false,
            recurringMonths: 1
        }]
    }]);

    const [clientInfo, setClientInfo] = useState<ProposalClientInfo>({
        name: 'Kliento vardas',
        company: 'Įmonės pavadinimas',
        proposalNumber: 'P-2025-001',
        date: new Date().toISOString().split('T')[0],
        validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        email: '',
        phone: '',
        address: ''
    });

    const [companyInfo, setCompanyInfo] = useState<ProposalCompanyInfo>({
        name: 'Jūsų įmonė, UAB',
        email: 'info@jusuimone.lt',
        phone: '+370 600 00000',
        address: 'Gatvės g. 1, Vilnius',
        website: 'www.jusuimone.lt',
        bankAccount: 'LT00 0000 0000 0000 0000',
        bankName: 'Banko pavadinimas',
        companyCode: '123456789',
        vatCode: 'LT123456789',
        logo: null
    });

    const [paymentTerms, setPaymentTerms] = useState<ProposalPaymentTerms>({
        enabled: false,
        stages: [
            { id: 1, name: 'Išankstinis mokėjimas', percentage: 30, description: 'Prieš pradedant projektą' },
            { id: 2, name: 'Tarpinis mokėjimas', percentage: 40, description: 'Projekto viduryje' },
            { id: 3, name: 'Galutinis mokėjimas', percentage: 30, description: 'Po projekto pabaigos' }
        ]
    });

    const [discount, setDiscount] = useState<ProposalDiscount>({
        enabled: false,
        type: 'percentage',
        value: 0
    });

    const [vat, setVat] = useState<ProposalVat>({
        enabled: true,
        rate: 21
    });

    const [terms, setTerms] = useState<ProposalTerms>({
        payment: 'Mokėjimas per 5 dienas nuo sąskaitos faktūros išrašymo.',
        validity: 'Šis pasiūlymas galioja 30 dienų.',
        additional: ''
    });

    const [savedProposals, setSavedProposals] = useState<SavedProposal[]>([]);
    const [templates, setTemplates] = useState<any[]>([]);

    // Loading states
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // New features state
    const [clients, setClients] = useState<SavedClient[]>([]);
    const [showClientPicker, setShowClientPicker] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [currency, setCurrency] = useState<Currency>('EUR');

    const [colors, setColors] = useState<ProposalColors>({
        primary: '#2F2F2F',      // Charcoal (minimalist)
        success: '#059669',      // Subtle green
        cost: '#D97706',         // Warm amber
        info: '#6B7280',         // Neutral gray
        textPrimary: '#1F2937',  // Dark text
        textSecondary: '#9CA3AF', // Light gray text
        background: '#F3F1E6'    // Cream background
    });

    // TRANSLATIONS
    const translations = {
        lt: {
            title: 'Komercinis pasiūlymas',
            client: 'Klientas',
            company: 'Įmonė',
            proposalNo: 'Pasiūlymo Nr.',
            date: 'Data',
            validUntil: 'Galioja iki',
            email: 'El. paštas',
            phone: 'Telefonas',
            address: 'Adresas',
            print: 'Spausdinti',
            showCosts: 'Rodyti kaštus',
            hideCosts: 'Slėpti kaštus',
            colorSettings: 'Spalvos',
            settings: 'Nustatymai',
            analytics: 'Analizė',
            templates: 'Šablonai',
            save: 'Išsaugoti',
            load: 'Įkelti',
            export: 'Eksportuoti',
            serviceSection: 'Paslaugų skyrius',
            addService: 'Pridėti paslaugą',
            addSection: 'Pridėti skyrių',
            newSection: 'Naujas skyrius',
            serviceDescription: 'PASLAUGOS APRAŠYMAS',
            unitPrice: 'VIENETO KAINA',
            quantity: 'KIEKIS',
            total: 'IŠ VISO',
            details: '📋 Detalės',
            addItem: 'Pridėti elementą',
            newService: 'Nauja paslauga',
            detailedItem: 'Detali pozicija',
            cost: '💰 Kaštai',
            addCostItem: 'Pridėti kaštų elementą',
            newCostItem: 'Naujas kaštų elementas',
            totalCost: 'Viso kaštai:',
            margin: 'Marža:',
            noServices: 'Šiame skyriuje nėra paslaugų',
            addFirstService: 'Pridėti pirmą paslaugą',
            sectionTotal: 'Skyriaus suma:',
            sectionCost: 'Skyriaus kaštai:',
            sectionMargin: 'Skyriaus marža:',
            grandTotal: 'Bendra suma',
            servicesSubtotal: 'Paslaugų tarpinė suma:',
            discount: 'Nuolaida:',
            subtotalAfterDiscount: 'Suma po nuolaidos:',
            vatAmount: 'PVM ({rate}%):',
            grandTotalLabel: 'BENDRA SUMA:',
            paymentSchedule: 'Mokėjimo grafikas',
            stage: 'Etapas',
            amount: 'Suma',
            companyInfo: 'Įmonės informacija',
            bankDetails: 'Banko rekvizitai',
            bankAccount: 'Sąskaitos Nr.',
            bankName: 'Bankas',
            companyCode: 'Įmonės kodas',
            vatCode: 'PVM kodas',
            termsAndConditions: 'Papildomos sąlygos',
            proposalValidity: 'Pasiūlymo galiojimas',
            additionalTerms: 'Papildomos sąlygos',
            uploadLogo: 'Įkelti logotipą',
            recurring: 'Pasikartojanti',
            months: 'mėn.',
            monthlyPrice: 'Mėnesio kaina',
            saveAsTemplate: 'Išsaugoti kaip šabloną',
            loadTemplate: 'Įkelti šabloną',
            deleteTemplate: 'Ištrinti šabloną',
            totalProposals: 'Viso pasiūlymų',
            avgMargin: 'Vid. marža',
            totalRevenue: 'Viso pajamų',
            website: 'Svetainė',
            enableDiscount: 'Įjungti nuolaidą',
            percentage: 'Procentais (%)',
            fixedAmount: 'Fiksuota suma (€)',
            enableVat: 'Įjungti PVM',
            enablePaymentSchedule: 'Įjungti mokėjimo grafiką',
            proposalSaved: 'Pasiūlymas išsaugotas!',
            templateSaved: 'Šablonas išsaugotas!',
            companyInfoSaved: 'Įmonės informacija išsaugota!',
            dataLoaded: 'Duomenys sėkmingai įkelti!',
            errorReading: 'Klaida skaitant failą!',
            enterTemplateName: 'Įveskite šablono pavadinimą:',
            savedProposals: 'Išsaugoti pasiūlymai',
            sections: 'skyriai',
            primaryColor: 'Pagrindinė',
            successColor: 'Sėkmės',
            costColor: 'Kaštų',
            infoColor: 'Info',
            textColor: 'Teksto',
            backgroundColor: 'Fono',
            logout: 'Atsijungti',
            adminPanelTitle: 'Administratoriaus skydelis',
            addNewUser: 'Pridėti naują vartotoją',
            addUserPlaceholder: 'vartotojas@email.com',
            addUserButton: 'Pridėti',
            authorizedUsers: 'Autorizuoti vartotojai',
            noUsersYet: 'Kol kas nėra pridėtų vartotojų.',
            userAddedSuccess: 'Vartotojas sėkmingai pridėtas.',
            userRemovedSuccess: 'Vartotojas pašalintas.',
            errorAddingUser: 'Klaida pridedant vartotoją.',
            errorRemovingUser: 'Klaida šalinant vartotoją.',
            adminHasAccess: 'Administratorius jau turi prieigą.',
            enterEmail: 'Įveskite el. pašto adresą.',
            // New translations
            selectClient: 'Pasirinkti klientą',
            newClient: 'Naujas klientas',
            savedClients: 'Išsaugoti klientai',
            saveClient: 'Išsaugoti klientą',
            searchProposals: 'Ieškoti pasiūlymų...',
            copyProposal: 'Kopijuoti',
            proposalCopied: 'Pasiūlymas nukopijuotas!',
            currency: 'Valiuta',
            dashboard: 'Suvestinė',
            versionHistory: 'Versijų istorija',
            version: 'Versija',
            signature: 'Parašas',
            clientSignature: 'Kliento parašas',
            acceptedBy: 'Priėmė',
            dateAccepted: 'Priėmimo data',
            noClients: 'Dar nėra išsaugotų klientų',
            clientSaved: 'Klientas išsaugotas!',
            filter: 'Filtruoti',
            all: 'Visi',
            thisMonth: 'Šį mėnesį',
            thisYear: 'Šiais metais',
            conversionRate: 'Konversija',
            avgDealSize: 'Vid. sandorio dydis',
            monthlyRevenue: 'Mėnesio pajamos'
        },
        en: {
            title: 'Commercial Proposal',
            client: 'Client',
            company: 'Company',
            proposalNo: 'Proposal No.',
            date: 'Date',
            validUntil: 'Valid Until',
            email: 'Email',
            phone: 'Phone',
            address: 'Address',
            print: 'Print',
            showCosts: 'Show Costs',
            hideCosts: 'Hide Costs',
            colorSettings: 'Colors',
            settings: 'Settings',
            analytics: 'Analytics',
            templates: 'Templates',
            save: 'Save',
            load: 'Load',
            export: 'Export',
            serviceSection: 'Service Section',
            addService: 'Add Service',
            addSection: 'Add Section',
            newSection: 'New Section',
            serviceDescription: 'SERVICE DESCRIPTION',
            unitPrice: 'UNIT PRICE',
            quantity: 'QUANTITY',
            total: 'TOTAL',
            details: '📋 Details',
            addItem: 'Add Item',
            newService: 'New Service',
            detailedItem: 'Detailed Item',
            cost: '💰 Cost',
            addCostItem: 'Add Cost Item',
            newCostItem: 'New Cost Item',
            totalCost: 'Total cost:',
            margin: 'Margin:',
            noServices: 'No services in this section',
            addFirstService: 'Add the first service',
            sectionTotal: 'Section Total:',
            sectionCost: 'Section Cost:',
            sectionMargin: 'Section Margin:',
            grandTotal: 'Grand Total',
            servicesSubtotal: 'Services Subtotal:',
            discount: 'Discount:',
            subtotalAfterDiscount: 'Subtotal after discount:',
            vatAmount: 'VAT ({rate}%):',
            grandTotalLabel: 'GRAND TOTAL:',
            paymentSchedule: 'Payment Schedule',
            stage: 'Stage',
            amount: 'Amount',
            companyInfo: 'Company Information',
            bankDetails: 'Bank Details',
            bankAccount: 'Account No.',
            bankName: 'Bank',
            companyCode: 'Company Code',
            vatCode: 'VAT Code',
            termsAndConditions: 'Payment Terms',
            proposalValidity: 'Proposal Validity',
            additionalTerms: 'Additional Terms',
            uploadLogo: 'Upload Logo',
            recurring: 'Recurring',
            months: 'mo.',
            monthlyPrice: 'Monthly Price',
            saveAsTemplate: 'Save as Template',
            loadTemplate: 'Load Template',
            deleteTemplate: 'Delete Template',
            totalProposals: 'Total Proposals',
            avgMargin: 'Avg. Margin',
            totalRevenue: 'Total Revenue',
            website: 'Website',
            enableDiscount: 'Enable discount',
            percentage: 'Percentage (%)',
            fixedAmount: 'Fixed amount (€)',
            enableVat: 'Enable VAT',
            enablePaymentSchedule: 'Enable payment schedule',
            proposalSaved: 'Proposal saved!',
            templateSaved: 'Template saved!',
            companyInfoSaved: 'Company info saved!',
            dataLoaded: 'Data loaded successfully!',
            errorReading: 'Error reading file!',
            enterTemplateName: 'Enter template name:',
            savedProposals: 'Saved Proposals',
            sections: 'sections',
            primaryColor: 'Primary',
            successColor: 'Success',
            costColor: 'Cost',
            infoColor: 'Info',
            textColor: 'Text',
            backgroundColor: 'Background',
            logout: 'Log Out',
            adminPanelTitle: 'Admin Panel',
            addNewUser: 'Add new user',
            addUserPlaceholder: 'user@email.com',
            addUserButton: 'Add',
            authorizedUsers: 'Authorized Users',
            noUsersYet: 'No users added yet.',
            userAddedSuccess: 'User added successfully.',
            userRemovedSuccess: 'User removed successfully.',
            errorAddingUser: 'Error adding user.',
            errorRemovingUser: 'Error removing user.',
            adminHasAccess: 'Admin already has access.',
            enterEmail: 'Please enter an email address.',
            // New translations
            selectClient: 'Select client',
            newClient: 'New client',
            savedClients: 'Saved Clients',
            saveClient: 'Save client',
            searchProposals: 'Search proposals...',
            copyProposal: 'Copy',
            proposalCopied: 'Proposal copied!',
            currency: 'Currency',
            dashboard: 'Dashboard',
            versionHistory: 'Version History',
            version: 'Version',
            signature: 'Signature',
            clientSignature: 'Client Signature',
            acceptedBy: 'Accepted by',
            dateAccepted: 'Date Accepted',
            noClients: 'No saved clients yet',
            clientSaved: 'Client saved!',
            filter: 'Filter',
            all: 'All',
            thisMonth: 'This month',
            thisYear: 'This year',
            conversionRate: 'Conversion',
            avgDealSize: 'Avg. Deal Size',
            monthlyRevenue: 'Monthly Revenue'
        }
    };

    const t = translations[language];

    // LOAD DATA ON MOUNT
    useEffect(() => {
        if (!user) return;

        const loadData = async () => {
            setIsLoading(true);
            try {
                // Load proposals
                const proposals = await firestoreService.getProposals(user.uid);
                setSavedProposals(proposals);

                // Load templates (shared across all users)
                const templates = await firestoreService.getProposalTemplates();
                setTemplates(templates);

                // Load company info (shared across all users)
                const company = await firestoreService.getCompanyDetails();
                if (company) {
                    setCompanyInfo(company);
                }

                // Load clients
                const clientsList = await firestoreService.getClients(user.uid);
                setClients(clientsList);

                // Get next proposal number
                const nextNumber = await firestoreService.getNextProposalNumber(user.uid);
                setClientInfo(prev => ({ ...prev, proposalNumber: nextNumber }));
            } catch (error) {
                console.error('Error loading data:', error);
                toast.error(language === 'lt' ? 'Klaida kraunant duomenis' : 'Error loading data');
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [user]);

    // Auto-resize textareas when content changes
    useEffect(() => {
        const textareas = document.querySelectorAll('textarea');
        textareas.forEach(textarea => {
            textarea.style.height = 'auto';
            textarea.style.height = textarea.scrollHeight + 'px';
        });
    }, [sections, terms]);

    // ADMIN PANEL EFFECT
    useEffect(() => {
        if (isAdmin) {
            const unsubscribe = db.collection("allowedUsers").onSnapshot(
                (snapshot: any) => {
                    const usersList = snapshot.docs.map((doc: any) => ({
                        id: doc.id,
                        ...doc.data()
                    }));
                    setAdminPanel(prev => ({ ...prev, allowedUsers: usersList }));
                },
                (error: any) => {
                    console.error("Klaida gaunant vartotojų sąrašą:", error);
                    toast.error(language === 'lt'
                        ? "Klaida gaunant vartotojų sąrašą. Patikrinkite Firestore taisykles."
                        : "Error fetching users. Check Firestore rules.");
                }
            );
            return () => unsubscribe();
        }
    }, [isAdmin, language]);

    // UTILITY FUNCTIONS
    const handleColorChange = (key: keyof ProposalColors, value: string) => setColors(prev => ({ ...prev, [key]: value }));
    const hexToRgba = (hex: string, alpha = 1) => {
        if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) return `rgba(230,230,230,${alpha})`;
        let c: any = hex.substring(1).split('');
        if (c.length === 3) c = [c[0], c[0], c[1], c[1], c[2], c[2]];
        c = '0x' + c.join('');
        return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')},${alpha})`;
    };
    const toRoman = (num: number) => {
        const r = [{ v: 10, n: 'X' }, { v: 9, n: 'IX' }, { v: 5, n: 'V' }, { v: 4, n: 'IV' }, { v: 1, n: 'I' }];
        let res = '';
        for (let { v, n } of r) {
            while (num >= v) {
                res += n;
                num -= v;
            }
        }
        return res;
    };

    // SECTION MANAGEMENT
    const addSection = () => setSections([...sections, {
        id: Date.now(),
        title: `${toRoman(sections.length + 1)}. ${t.newSection}`,
        services: []
    }]);
    const removeSection = (sectionId: number) => setSections(sections.filter(s => s.id !== sectionId));
    const updateSectionTitle = (sectionId: number, title: string) => setSections(sections.map(s => s.id === sectionId ? { ...s, title } : s));

    // SERVICE MANAGEMENT
    const addService = (sectionId: number) => {
        setSections(sections.map(s => s.id === sectionId ? {
            ...s,
            services: [...s.services, {
                id: Date.now(),
                name: t.newService,
                unitPrice: 0,
                quantity: 1,
                expanded: false,
                details: [],
                costs: [],
                isRecurring: false,
                recurringMonths: 1
            }]
        } : s));
    };
    const removeService = (sectionId: number, serviceId: number) => {
        setSections(sections.map(s => s.id === sectionId ? {
            ...s,
            services: s.services.filter(srv => srv.id !== serviceId)
        } : s));
    };
    const updateService = (sectionId: number, serviceId: number, field: string, value: any) => {
        setSections(sections.map(s => s.id === sectionId ? {
            ...s,
            services: s.services.map(srv => srv.id === serviceId ? { ...srv, [field]: value } : srv)
        } : s));
    };
    const toggleServiceExpand = (sectionId: number, serviceId: number) => {
        setSections(sections.map(s => s.id === sectionId ? {
            ...s,
            services: s.services.map(srv => srv.id === serviceId ? { ...srv, expanded: !srv.expanded } : srv)
        } : s));
    };

    // DETAIL MANAGEMENT
    const addDetail = (sectionId: number, serviceId: number) => {
        setSections(sections.map(s => s.id === sectionId ? {
            ...s,
            services: s.services.map(srv => srv.id === serviceId ? {
                ...srv,
                details: [...srv.details, { id: Date.now(), name: t.detailedItem, unitPrice: 0, quantity: 1 }]
            } : srv)
        } : s));
    };
    const removeDetail = (sectionId: number, serviceId: number, detailId: number) => {
        setSections(sections.map(s => s.id === sectionId ? {
            ...s,
            services: s.services.map(srv => srv.id === serviceId ? {
                ...srv,
                details: srv.details.filter(d => d.id !== detailId)
            } : srv)
        } : s));
    };
    const updateDetail = (sectionId: number, serviceId: number, detailId: number, field: string, value: any) => {
        setSections(sections.map(s => s.id === sectionId ? {
            ...s,
            services: s.services.map(srv => srv.id === serviceId ? {
                ...srv,
                details: srv.details.map(d => d.id === detailId ? { ...d, [field]: value } : d)
            } : srv)
        } : s));
    };

    // COST MANAGEMENT
    const addCost = (sectionId: number, serviceId: number) => {
        setSections(sections.map(s => s.id === sectionId ? {
            ...s,
            services: s.services.map(srv => srv.id === serviceId ? {
                ...srv,
                costs: [...srv.costs, { id: Date.now(), name: t.newCostItem, unitCost: 0, quantity: 1 }]
            } : srv)
        } : s));
    };
    const removeCost = (sectionId: number, serviceId: number, costId: number) => {
        setSections(sections.map(s => s.id === sectionId ? {
            ...s,
            services: s.services.map(srv => srv.id === serviceId ? {
                ...srv,
                costs: srv.costs.filter(c => c.id !== costId)
            } : srv)
        } : s));
    };
    const updateCost = (sectionId: number, serviceId: number, costId: number, field: string, value: any) => {
        setSections(sections.map(s => s.id === sectionId ? {
            ...s,
            services: s.services.map(srv => srv.id === serviceId ? {
                ...srv,
                costs: srv.costs.map(c => c.id === costId ? { ...c, [field]: value } : c)
            } : srv)
        } : s));
    };

    // CALCULATIONS
    const calculateServiceTotal = (service: any) => {
        const baseTotal = service.details.length > 0
            ? service.details.reduce((sum: number, d: any) => sum + (d.unitPrice * d.quantity), 0)
            : service.unitPrice * service.quantity;

        return service.isRecurring ? baseTotal * service.recurringMonths : baseTotal;
    };
    const calculateServiceCost = (service: any) => service.costs.reduce((sum: number, c: any) => sum + (c.unitCost * c.quantity), 0);
    const calculateServiceMargin = (service: any) => calculateServiceTotal(service) - calculateServiceCost(service);
    const calculateServiceMarginPercent = (service: any) => {
        const total = calculateServiceTotal(service);
        return total === 0 ? 0 : Math.round(((calculateServiceMargin(service) / total) * 100));
    };
    const calculateSectionTotal = (section: any) => section.services.reduce((sum: number, srv: any) => sum + calculateServiceTotal(srv), 0);
    const calculateSectionCost = (section: any) => section.services.reduce((sum: number, srv: any) => sum + calculateServiceCost(srv), 0);
    const calculateSectionMargin = (section: any) => calculateSectionTotal(section) - calculateSectionCost(section);
    const calculateGrandTotal = () => sections.reduce((sum, s) => sum + calculateSectionTotal(s), 0);
    const calculateGrandCost = () => sections.reduce((sum, s) => sum + calculateSectionCost(s), 0);
    const calculateGrandMargin = () => calculateGrandTotal() - calculateGrandCost();
    const calculateGrandMarginPercent = () => {
        const total = calculateGrandTotal();
        return total === 0 ? 0 : Math.round(((calculateGrandMargin() / total) * 100));
    };

    const calculateDiscountAmount = () => {
        if (!discount.enabled) return 0;
        const subtotal = calculateGrandTotal();
        return discount.type === 'percentage'
            ? (subtotal * discount.value / 100)
            : discount.value;
    };

    const calculateSubtotalAfterDiscount = () => {
        return calculateGrandTotal() - calculateDiscountAmount();
    };

    const calculateVatAmount = () => {
        if (!vat.enabled) return 0;
        return calculateSubtotalAfterDiscount() * (vat.rate / 100);
    };

    const calculateFinalTotal = () => {
        return calculateSubtotalAfterDiscount() + calculateVatAmount();
    };



    // SAVE PROPOSAL
    const saveProposal = useCallback(async () => {
        if (!user || isSaving) return;

        setIsSaving(true);
        const proposalData = {
            date: '',
            sections,
            clientInfo,
            discount,
            vat,
            total: calculateFinalTotal(),
            status: 'draft' as ProposalStatus
        };

        try {
            const newProposal = await firestoreService.addProposal(user.uid, proposalData);
            setSavedProposals([newProposal, ...savedProposals]);
            toast.success(t.proposalSaved);
        } catch (error) {
            console.error('Error saving proposal:', error);
            toast.error(language === 'lt' ? 'Klaida išsaugant pasiūlymą' : 'Error saving proposal');
        } finally {
            setIsSaving(false);
        }
    }, [user, isSaving, sections, clientInfo, discount, vat, savedProposals, t.proposalSaved, language]);

    // LOAD PROPOSAL
    const loadProposal = (proposal: any) => {
        setClientInfo(proposal.clientInfo);
        setSections(proposal.sections);
        setDiscount(proposal.discount);
        setVat(proposal.vat);
    };

    // DELETE PROPOSAL
    const deleteProposal = async (id: string) => {
        if (!user || !window.confirm(language === 'lt' ? 'Ar tikrai norite ištrinti šį pasiūlymą?' : 'Delete this proposal?')) return;

        try {
            await firestoreService.deleteProposal(user.uid, id);
            setSavedProposals(savedProposals.filter(p => p.id !== id));
            toast.success(language === 'lt' ? 'Pasiūlymas ištrintas' : 'Proposal deleted');
        } catch (error) {
            console.error('Error deleting proposal:', error);
            toast.error(language === 'lt' ? 'Klaida trinant pasiūlymą' : 'Error deleting proposal');
        }
    };

    // UPDATE PROPOSAL STATUS
    const updateProposalStatus = async (proposalId: string, newStatus: ProposalStatus) => {
        if (!user) return;

        const statusUpdate: Partial<SavedProposal> = { status: newStatus };

        // Add timestamp based on status
        if (newStatus === 'sent') statusUpdate.sentAt = new Date().toISOString();
        if (newStatus === 'accepted') statusUpdate.acceptedAt = new Date().toISOString();
        if (newStatus === 'rejected') statusUpdate.rejectedAt = new Date().toISOString();

        try {
            await firestoreService.updateProposal(user.uid, proposalId, statusUpdate);
            setSavedProposals(savedProposals.map(p =>
                p.id === proposalId ? { ...p, ...statusUpdate } : p
            ));

            const statusLabels: Record<ProposalStatus, { lt: string; en: string }> = {
                draft: { lt: 'Juodraštis', en: 'Draft' },
                sent: { lt: 'Išsiųstas', en: 'Sent' },
                accepted: { lt: 'Priimtas', en: 'Accepted' },
                rejected: { lt: 'Atmestas', en: 'Rejected' },
                expired: { lt: 'Pasibaigęs', en: 'Expired' }
            };

            toast.success(language === 'lt'
                ? `Statusas pakeistas į: ${statusLabels[newStatus].lt}`
                : `Status changed to: ${statusLabels[newStatus].en}`);
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error(language === 'lt' ? 'Klaida keičiant statusą' : 'Error updating status');
        }
    };

    // Get status badge color
    const getStatusColor = (status?: ProposalStatus): string => {
        switch (status) {
            case 'draft': return 'bg-gray-200 text-gray-700';
            case 'sent': return 'bg-blue-200 text-blue-700';
            case 'accepted': return 'bg-green-200 text-green-700';
            case 'rejected': return 'bg-red-200 text-red-700';
            case 'expired': return 'bg-orange-200 text-orange-700';
            default: return 'bg-gray-200 text-gray-700';
        }
    };

    const getStatusLabel = (status?: ProposalStatus): string => {
        const labels: Record<ProposalStatus, { lt: string; en: string }> = {
            draft: { lt: 'Juodraštis', en: 'Draft' },
            sent: { lt: 'Išsiųstas', en: 'Sent' },
            accepted: { lt: 'Priimtas', en: 'Accepted' },
            rejected: { lt: 'Atmestas', en: 'Rejected' },
            expired: { lt: 'Pasibaigęs', en: 'Expired' }
        };
        return labels[status || 'draft'][language];
    };

    // CURRENCY FORMATTING
    const currencySymbols: Record<Currency, string> = {
        EUR: '€',
        USD: '$',
        GBP: '£'
    };

    const formatCurrency = (amount: number): string => {
        const symbol = currencySymbols[currency];
        return `${amount.toFixed(2)} ${symbol}`;
    };

    // CLIENT MANAGEMENT
    const saveCurrentClient = async () => {
        if (!user) return;

        const clientData = {
            name: clientInfo.name,
            company: clientInfo.company,
            email: clientInfo.email,
            phone: clientInfo.phone,
            address: clientInfo.address,
            createdAt: ''
        };

        try {
            const newClient = await firestoreService.addClient(user.uid, clientData);
            setClients([...clients, newClient]);
            toast.success(t.clientSaved);
        } catch (error) {
            console.error('Error saving client:', error);
            toast.error(language === 'lt' ? 'Klaida išsaugant klientą' : 'Error saving client');
        }
    };

    const selectClient = (client: SavedClient) => {
        setClientInfo({
            ...clientInfo,
            name: client.name,
            company: client.company,
            email: client.email,
            phone: client.phone,
            address: client.address
        });
        setShowClientPicker(false);
    };

    // COPY PROPOSAL
    const copyProposal = async (proposalId: string) => {
        if (!user) return;

        try {
            const copiedProposal = await firestoreService.copyProposal(user.uid, proposalId);
            setSavedProposals([copiedProposal, ...savedProposals]);
            toast.success(t.proposalCopied);
        } catch (error) {
            console.error('Error copying proposal:', error);
            toast.error(language === 'lt' ? 'Klaida kopijuojant pasiūlymą' : 'Error copying proposal');
        }
    };

    // FILTER PROPOSALS
    const filteredProposals = savedProposals.filter(p => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            p.clientInfo.name.toLowerCase().includes(query) ||
            p.clientInfo.company.toLowerCase().includes(query) ||
            p.clientInfo.proposalNumber.toLowerCase().includes(query)
        );
    });

    // TEMPLATES
    const saveAsTemplate = async () => {
        if (!user) return;
        const templateName = prompt(t.enterTemplateName);
        if (!templateName) return;

        const templateData = {
            name: templateName,
            sections: sections
        };

        try {
            const newTemplate = await firestoreService.addProposalTemplate(user.uid, user.email || '', templateData);
            setTemplates([...templates, newTemplate]);
            toast.success(t.templateSaved);
        } catch (error) {
            console.error('Error saving template:', error);
            toast.error(language === 'lt' ? 'Klaida išsaugant šabloną' : 'Error saving template');
        }
    };

    const loadTemplate = (template: any) => {
        setSections(template.sections);
    };

    const deleteTemplate = async (id: string) => {
        if (!user || !window.confirm(language === 'lt' ? 'Ar tikrai norite ištrinti šį šabloną?' : 'Delete this template?')) return;

        try {
            await firestoreService.deleteProposalTemplate(id);
            setTemplates(templates.filter(t => t.id !== id));
            toast.success(language === 'lt' ? 'Šablonas ištrintas' : 'Template deleted');
        } catch (error) {
            console.error('Error deleting template:', error);
            toast.error(language === 'lt' ? 'Klaida trinant šabloną' : 'Error deleting template');
        }
    };

    // EXPORT
    const exportToJSON = () => {
        const data = {
            clientInfo,
            companyInfo,
            sections,
            discount,
            vat,
            paymentTerms,
            terms
        };
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `proposal-${clientInfo.proposalNumber}.json`;
        a.click();
    };

    const importFromJSON = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const data = JSON.parse(event.target?.result as string);
                setClientInfo(data.clientInfo);
                setCompanyInfo(data.companyInfo);
                setSections(data.sections);
                setDiscount(data.discount);
                setVat(data.vat);
                setPaymentTerms(data.paymentTerms);
                setTerms(data.terms);
                toast.success(t.dataLoaded);
            } catch (err) {
                toast.error(t.errorReading);
            }
        };
        reader.readAsText(file);
    };

    // COMPANY INFO
    const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!APP_CONFIG.SUPPORTED_LOGO_FORMATS.includes(file.type)) {
            toast.error(language === 'lt'
                ? 'Netinkamas failo formatas. Palaikomi: JPG, PNG, SVG'
                : 'Invalid file format. Supported: JPG, PNG, SVG');
            return;
        }

        // Validate file size
        if (file.size > APP_CONFIG.MAX_LOGO_SIZE_BYTES) {
            toast.error(language === 'lt'
                ? 'Failas per didelis. Maksimalus dydis: 5MB'
                : 'File too large. Maximum size: 5MB');
            return;
        }

        const reader = new FileReader();
        reader.onload = async (event) => {
            const newCompanyInfo = { ...companyInfo, logo: event.target?.result as string };
            setCompanyInfo(newCompanyInfo);
            if (user) {
                try {
                    await firestoreService.updateCompanyDetails(newCompanyInfo, user.email || '');
                    toast.success(language === 'lt' ? 'Logotipas įkeltas' : 'Logo uploaded');
                } catch (error) {
                    console.error('Error saving logo:', error);
                    toast.error(language === 'lt' ? 'Klaida įkeliant logotipą' : 'Error uploading logo');
                }
            }
        };
        reader.readAsDataURL(file);
    };

    const saveCompanyInfo = async () => {
        if (!user) return;
        try {
            await firestoreService.updateCompanyDetails(companyInfo, user.email || '');
            toast.success(t.companyInfoSaved);
        } catch (error) {
            console.error('Error saving company info:', error);
            toast.error(language === 'lt' ? 'Klaida išsaugant įmonės informaciją' : 'Error saving company info');
        }
    };

    const handlePrint = () => window.print();

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Ctrl/Cmd + S = Save
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                saveProposal();
            }
            // Ctrl/Cmd + P = Print
            if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
                e.preventDefault();
                handlePrint();
            }
            // Escape = Close panels
            if (e.key === 'Escape') {
                setShowSettings(false);
                setShowAnalytics(false);
                setShowTemplates(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [saveProposal]);

    // ANALYTICS - Calculate real margin from saved proposals
    const getAnalytics = () => {
        let totalMarginPercent = 0;
        let totalRevenue = 0;
        let totalCost = 0;

        savedProposals.forEach(proposal => {
            // Calculate revenue and cost from proposal sections
            let proposalRevenue = 0;
            let proposalCost = 0;

            if (proposal.sections && Array.isArray(proposal.sections)) {
                proposal.sections.forEach(section => {
                    if (section.services && Array.isArray(section.services)) {
                        section.services.forEach(service => {
                            // Calculate service total
                            const serviceTotal = service.details && service.details.length > 0
                                ? service.details.reduce((sum: number, d: any) => sum + (d.unitPrice * d.quantity), 0)
                                : (service.unitPrice * service.quantity);

                            const adjustedTotal = service.isRecurring
                                ? serviceTotal * (service.recurringMonths || 1)
                                : serviceTotal;

                            proposalRevenue += adjustedTotal;

                            // Calculate service cost
                            if (service.costs && Array.isArray(service.costs)) {
                                const serviceCost = service.costs.reduce((sum: number, c: any) =>
                                    sum + (c.unitCost * c.quantity), 0);
                                proposalCost += serviceCost;
                            }
                        });
                    }
                });
            }

            totalRevenue += proposalRevenue;
            totalCost += proposalCost;

            // Calculate margin percent for this proposal
            if (proposalRevenue > 0) {
                const marginPercent = ((proposalRevenue - proposalCost) / proposalRevenue) * 100;
                totalMarginPercent += marginPercent;
            }
        });

        return {
            totalProposals: savedProposals.length,
            avgMargin: savedProposals.length > 0
                ? totalMarginPercent / savedProposals.length
                : 0,
            totalRevenue,
            totalCost,
            totalMargin: totalRevenue - totalCost,
            overallMarginPercent: totalRevenue > 0
                ? ((totalRevenue - totalCost) / totalRevenue) * 100
                : 0
        };
    };

    // ADMIN FUNCTIONS
    const handleAdminEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAdminPanel(prev => ({ ...prev, newUserEmail: e.target.value }));
    };

    const addAllowedUser = async () => {
        const emailToAdd = adminPanel.newUserEmail.trim().toLowerCase();
        if (!emailToAdd) {
            toast.warning(t.enterEmail);
            return;
        }
        if (emailToAdd === APP_CONFIG.ADMIN_EMAIL.toLowerCase()) {
            toast.info(t.adminHasAccess);
            return;
        }

        try {
            await db.collection("allowedUsers").doc(emailToAdd).set({
                addedAt: new Date().toISOString(),
                addedBy: user.email
            });
            toast.success(t.userAddedSuccess);
            setAdminPanel(prev => ({ ...prev, newUserEmail: '' }));
        } catch (error) {
            console.error("Klaida pridedant vartotoją:", error);
            toast.error(t.errorAddingUser);
        }
    };

    const removeAllowedUser = async (emailToRemove: string) => {
        if (!window.confirm(language === 'lt' ? `Ar tikrai norite pašalinti ${emailToRemove}?` : `Remove ${emailToRemove}?`)) return;

        try {
            await db.collection("allowedUsers").doc(emailToRemove).delete();
            toast.success(t.userRemovedSuccess);
        } catch (error) {
            console.error("Klaida šalinant vartotoją:", error);
            toast.error(t.errorRemovingUser);
        }
    };

    const calculations = {
        hexToRgba,
        calculateServiceTotal,
        calculateServiceCost,
        calculateServiceMargin,
        calculateServiceMarginPercent,
        calculateSectionTotal,
        calculateSectionCost,
        calculateSectionMargin
    };

    // Loading skeleton
    if (isLoading) {
        return (
            <div className="min-h-screen p-4" style={{ backgroundColor: colors.background }}>
                <div className="max-w-7xl mx-auto">
                    <div className="bg-white rounded-md shadow-sm p-6 animate-pulse">
                        <div className="flex justify-between">
                            <div className="space-y-3">
                                <div className="h-6 w-48 bg-gray-200 rounded"></div>
                                <div className="h-4 w-32 bg-gray-200 rounded"></div>
                                <div className="h-4 w-40 bg-gray-200 rounded"></div>
                            </div>
                            <div className="flex gap-2">
                                <div className="h-9 w-24 bg-gray-200 rounded-lg"></div>
                                <div className="h-9 w-24 bg-gray-200 rounded-lg"></div>
                                <div className="h-9 w-32 bg-gray-200 rounded-lg"></div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-md shadow-sm p-6 mt-4 animate-pulse">
                        <div className="h-6 w-40 bg-gray-200 rounded mb-4"></div>
                        <div className="space-y-3">
                            <div className="h-16 bg-gray-100 rounded-lg"></div>
                            <div className="h-16 bg-gray-100 rounded-lg"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4 print:p-0" style={{ backgroundColor: colors.background }}>
            <div className="max-w-7xl mx-auto">
                {/* HEADER */}
                <div className="bg-white rounded-md shadow-sm mb-3 p-4 print:shadow-none print:mb-2" style={{ borderTop: `1px solid ${colors.primary}` }}>
                    <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                            <h1 className="text-base font-normal mb-3 print:text-sm" style={{ color: colors.textPrimary }}>{t.title}</h1>

                            <div className="space-y-2 max-w-2xl">
                                {/* Proposal Number - Auto-generated */}
                                <div className="flex items-center gap-2 print:block">
                                    <label className="text-xs font-normal" style={{ color: colors.textSecondary }}>{t.proposalNo}:</label>
                                    <span className="text-sm font-medium px-2 py-1 bg-gray-100 rounded print:bg-transparent print:px-0 print:py-0" style={{ color: colors.textPrimary }}>{clientInfo.proposalNumber}</span>
                                </div>

                                {/* Client Picker - only in edit mode */}
                                <div className="relative print:hidden">
                                    <label className="text-xs font-normal" style={{ color: colors.textSecondary }}>{t.client}:</label>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setShowClientPicker(!showClientPicker)}
                                            className="px-3 py-1.5 text-sm border rounded bg-white hover:bg-gray-50 flex items-center gap-1"
                                        >
                                            {t.selectClient}
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={saveCurrentClient}
                                            className="px-3 py-1.5 text-sm border rounded bg-green-50 text-green-700 hover:bg-green-100"
                                            title={t.saveClient}
                                        >
                                            {t.saveClient}
                                        </button>
                                    </div>
                                    {showClientPicker && (
                                        <div className="absolute z-10 mt-1 w-80 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                            {clients.length === 0 ? (
                                                <div className="p-4 text-center text-gray-500 text-sm">{t.noClients}</div>
                                            ) : (
                                                clients.map(client => (
                                                    <button
                                                        key={client.id}
                                                        onClick={() => selectClient(client)}
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b last:border-b-0"
                                                    >
                                                        <div className="font-medium text-sm">{client.company}</div>
                                                        <div className="text-xs text-gray-500">{client.name} • {client.email}</div>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Client info for print */}
                                <div className="hidden print:block">
                                    <label className="text-xs font-normal" style={{ color: colors.textSecondary }}>{t.client}:</label>
                                    <div className="text-sm" style={{ color: colors.textPrimary }}>{clientInfo.name}</div>
                                </div>

                                {/* Company name */}
                                <div>
                                    <label className="text-xs print:hidden font-normal" style={{ color: colors.textSecondary }}>{t.company}:</label>
                                    <input type="text" value={clientInfo.company} onChange={(e) => setClientInfo({ ...clientInfo, company: e.target.value })} className="block w-full px-3 py-2 text-sm border rounded print:hidden min-w-[400px]" style={{ color: colors.textPrimary }} />
                                    <div className="hidden print:block text-sm" style={{ color: colors.textPrimary }}>{clientInfo.company}</div>
                                </div>

                                {/* Date */}
                                <div>
                                    <label className="text-xs font-normal" style={{ color: colors.textSecondary }}>{t.date}:</label>
                                    <input type="text" value={clientInfo.date} onChange={(e) => setClientInfo({ ...clientInfo, date: e.target.value })} className="block w-full px-3 py-2 text-sm border rounded print:hidden min-w-[200px] max-w-[250px]" style={{ color: colors.textPrimary }} placeholder="YYYY-MM-DD" />
                                    <div className="hidden print:block text-sm" style={{ color: colors.textPrimary }}>{clientInfo.date}</div>
                                </div>

                                {/* Currency Selector - only in edit mode */}
                                <div className="print:hidden">
                                    <label className="text-xs font-normal" style={{ color: colors.textSecondary }}>{t.currency}:</label>
                                    <select
                                        value={currency}
                                        onChange={(e) => setCurrency(e.target.value as Currency)}
                                        className="block px-3 py-1.5 text-sm border rounded bg-white"
                                    >
                                        <option value="EUR">EUR (€)</option>
                                        <option value="USD">USD ($)</option>
                                        <option value="GBP">GBP (£)</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="ml-8">
                            {companyInfo.logo && (
                                <img src={companyInfo.logo} alt="Logo" className="logo-preview" style={{ maxWidth: '200px', maxHeight: '80px', objectFit: 'contain' }} />
                            )}
                        </div>

                        {/* TOOLBAR - Modern redesign */}
                        <div className="print:hidden">
                            <div className="flex items-center gap-3 flex-wrap">

                                {/* USER & LANGUAGE */}
                                <div className="flex items-center gap-2 pr-3 border-r border-gray-200">
                                    <div className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded-lg border">
                                        <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                                            {(user.displayName || user.email || '?')[0].toUpperCase()}
                                        </div>
                                        <div className="text-xs">
                                            <div className="font-medium text-gray-700 truncate max-w-[100px]" title={user.email || ''}>
                                                {user.displayName || user.email?.split('@')[0]}
                                            </div>
                                            <button
                                                onClick={() => auth.signOut()}
                                                className="text-red-500 hover:text-red-700 font-medium flex items-center gap-0.5"
                                            >
                                                <LogOut /> {t.logout}
                                            </button>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setLanguage(language === 'lt' ? 'en' : 'lt')}
                                        className="w-9 h-9 rounded-lg border border-gray-200 hover:bg-gray-100 flex items-center justify-center text-sm font-medium transition-colors"
                                        title={language === 'lt' ? 'Switch to English' : 'Perjungti į lietuvių k.'}
                                    >
                                        {language === 'lt' ? '🇬🇧' : '🇱🇹'}
                                    </button>
                                </div>

                                {/* PRINT */}
                                <div className="flex items-center gap-1.5 pr-3 border-r border-gray-200">
                                    <Button onClick={handlePrint} variant="secondary" size="sm" title={t.print}>
                                        <Printer /> <span className="hidden sm:inline">{t.print}</span>
                                    </Button>
                                </div>

                                {/* VIEW TOGGLE */}
                                <div className="flex items-center gap-1.5 pr-3 border-r border-gray-200">
                                    <button
                                        onClick={() => setShowCosts(!showCosts)}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                            showCosts
                                                ? 'bg-amber-100 text-amber-700 border border-amber-300'
                                                : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                                        }`}
                                        title={showCosts ? t.hideCosts : t.showCosts}
                                    >
                                        {showCosts ? <EyeOff /> : <Eye />}
                                        <span className="hidden md:inline">{showCosts ? t.hideCosts : t.showCosts}</span>
                                    </button>
                                </div>

                                {/* PANELS */}
                                <div className="flex items-center gap-1.5 pr-3 border-r border-gray-200">
                                    <button
                                        onClick={() => { setShowSettings(!showSettings); setShowAnalytics(false); setShowTemplates(false); }}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                            showSettings
                                                ? 'bg-gray-800 text-white'
                                                : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                                        }`}
                                        title={t.settings}
                                    >
                                        <Settings />
                                        <span className="hidden sm:inline">{t.settings}</span>
                                    </button>
                                    <button
                                        onClick={() => { setShowAnalytics(!showAnalytics); setShowSettings(false); setShowTemplates(false); }}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                            showAnalytics
                                                ? 'bg-green-600 text-white'
                                                : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                                        }`}
                                        title={t.analytics}
                                    >
                                        <BarChart />
                                        <span className="hidden sm:inline">{t.analytics}</span>
                                    </button>
                                    <button
                                        onClick={() => { setShowTemplates(!showTemplates); setShowSettings(false); setShowAnalytics(false); }}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                            showTemplates
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                                        }`}
                                        title={t.templates}
                                    >
                                        <Copy />
                                        <span className="hidden sm:inline">{t.templates}</span>
                                    </button>
                                </div>

                                {/* PRIMARY ACTIONS */}
                                <div className="flex items-center gap-2">
                                    <Button
                                        onClick={saveProposal}
                                        variant="primary"
                                        size="lg"
                                        loading={isSaving}
                                        title={`${t.save} (Ctrl+S)`}
                                        className="shadow-md hover:shadow-lg"
                                    >
                                        <Save /> {t.save}
                                    </Button>

                                    {/* Import/Export dropdown-style */}
                                    <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                                        <button
                                            onClick={exportToJSON}
                                            className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 text-xs font-medium border-r border-gray-200 transition-colors"
                                            title={language === 'lt' ? 'Eksportuoti JSON' : 'Export JSON'}
                                        >
                                            <Download />
                                        </button>
                                        <label
                                            className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 cursor-pointer text-xs font-medium transition-colors"
                                            title={language === 'lt' ? 'Importuoti JSON' : 'Import JSON'}
                                        >
                                            <Upload />
                                            <input type="file" accept=".json" onChange={importFromJSON} className="hidden" />
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SETTINGS PANEL */}
                    {showSettings && (
                        <ProposalSettings
                            t={t}
                            colors={colors}
                            companyInfo={companyInfo}
                            setCompanyInfo={setCompanyInfo}
                            discount={discount}
                            setDiscount={setDiscount}
                            vat={vat}
                            setVat={setVat}
                            paymentTerms={paymentTerms}
                            setPaymentTerms={setPaymentTerms}
                            terms={terms}
                            setTerms={setTerms}
                            handleLogoUpload={handleLogoUpload}
                            saveCompanyInfo={saveCompanyInfo}
                            handleColorChange={handleColorChange}
                            isAdmin={isAdmin}
                            adminPanel={adminPanel}
                            handleAdminEmailChange={handleAdminEmailChange}
                            addAllowedUser={addAllowedUser}
                            removeAllowedUser={removeAllowedUser}
                        />
                    )}

                    {/* ANALYTICS PANEL */}
                    {showAnalytics && (
                        <div className="border-t-2 pt-4 mt-4 print:hidden" style={{ borderColor: colors.primary }}>
                            <h3 className="text-lg font-semibold mb-3">{t.analytics}</h3>

                            {/* KPI Cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
                                <div className="border rounded-lg p-3 bg-blue-50">
                                    <div className="text-xs text-gray-600">{t.totalProposals}</div>
                                    <div className="text-2xl font-bold text-blue-600">{getAnalytics().totalProposals}</div>
                                </div>
                                <div className="border rounded-lg p-3 bg-green-50">
                                    <div className="text-xs text-gray-600">{t.avgMargin}</div>
                                    <div className="text-2xl font-bold text-green-600">{getAnalytics().avgMargin.toFixed(1)}%</div>
                                </div>
                                <div className="border rounded-lg p-3 bg-purple-50">
                                    <div className="text-xs text-gray-600">{t.totalRevenue}</div>
                                    <div className="text-2xl font-bold text-purple-600">{formatCurrency(getAnalytics().totalRevenue)}</div>
                                </div>
                                <div className="border rounded-lg p-3 bg-orange-50">
                                    <div className="text-xs text-gray-600">{language === 'lt' ? 'Viso kaštų' : 'Total Cost'}</div>
                                    <div className="text-2xl font-bold text-orange-600">{formatCurrency(getAnalytics().totalCost)}</div>
                                </div>
                                <div className="border rounded-lg p-3 bg-emerald-50">
                                    <div className="text-xs text-gray-600">{language === 'lt' ? 'Viso pelnas' : 'Total Profit'}</div>
                                    <div className="text-2xl font-bold text-emerald-600">{formatCurrency(getAnalytics().totalMargin)}</div>
                                </div>
                                <div className="border rounded-lg p-3 bg-cyan-50">
                                    <div className="text-xs text-gray-600">{language === 'lt' ? 'Bendra marža' : 'Overall Margin'}</div>
                                    <div className="text-2xl font-bold text-cyan-600">{getAnalytics().overallMarginPercent.toFixed(1)}%</div>
                                </div>
                            </div>

                            {/* Status breakdown with conversion rate */}
                            <div className="grid grid-cols-5 gap-2 mb-4">
                                {(['draft', 'sent', 'accepted', 'rejected', 'expired'] as ProposalStatus[]).map(status => {
                                    const count = savedProposals.filter(p => (p.status || 'draft') === status).length;
                                    const total = savedProposals.filter(p => (p.status || 'draft') === status).reduce((sum, p) => sum + p.total, 0);
                                    return (
                                        <div key={status} className={`p-2 rounded-lg text-center ${getStatusColor(status)}`}>
                                            <div className="text-lg font-bold">{count}</div>
                                            <div className="text-xs">{getStatusLabel(status)}</div>
                                            <div className="text-xs mt-1">{formatCurrency(total)}</div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Conversion Rate Card */}
                            {savedProposals.length > 0 && (
                                <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-sm text-gray-600">{t.conversionRate}</div>
                                            <div className="text-3xl font-bold text-green-600">
                                                {((savedProposals.filter(p => p.status === 'accepted').length / savedProposals.length) * 100).toFixed(1)}%
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-sm text-gray-600">{t.avgDealSize}</div>
                                            <div className="text-xl font-bold text-green-700">
                                                {formatCurrency(savedProposals.filter(p => p.status === 'accepted').reduce((sum, p) => sum + p.total, 0) / Math.max(1, savedProposals.filter(p => p.status === 'accepted').length))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Search and filter */}
                            <div className="flex items-center gap-3 mb-4">
                                <div className="flex-1 relative">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder={t.searchProposals}
                                        className="w-full px-4 py-2 pl-10 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                            </div>

                            <div className="mt-4">
                                <h4 className="font-semibold mb-2">{t.savedProposals} ({filteredProposals.length})</h4>
                                {filteredProposals.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500 border rounded-lg bg-gray-50">
                                        <p className="mb-2">{searchQuery ? (language === 'lt' ? 'Nieko nerasta' : 'Nothing found') : (language === 'lt' ? 'Dar nėra išsaugotų pasiūlymų' : 'No saved proposals yet')}</p>
                                        {!searchQuery && <p className="text-sm">{language === 'lt' ? 'Spauskite "Išsaugoti" mygtuką viršuje' : 'Click the "Save" button above'}</p>}
                                    </div>
                                ) : (
                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                        {filteredProposals.map(p => (
                                            <div key={p.id} className="flex justify-between items-center p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-mono text-gray-500">{p.clientInfo.proposalNumber}</span>
                                                        <span className="font-semibold">{p.clientInfo.company}</span>
                                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(p.status)}`}>
                                                            {getStatusLabel(p.status)}
                                                        </span>
                                                        {p.version && p.version > 1 && (
                                                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                                                                v{p.version}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-600">{new Date(p.date).toLocaleDateString()} • {formatCurrency(p.total)}</div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <select
                                                        value={p.status || 'draft'}
                                                        onChange={(e) => updateProposalStatus(p.id, e.target.value as ProposalStatus)}
                                                        className="px-2 py-1 text-xs border rounded bg-white"
                                                    >
                                                        <option value="draft">{language === 'lt' ? 'Juodraštis' : 'Draft'}</option>
                                                        <option value="sent">{language === 'lt' ? 'Išsiųstas' : 'Sent'}</option>
                                                        <option value="accepted">{language === 'lt' ? 'Priimtas' : 'Accepted'}</option>
                                                        <option value="rejected">{language === 'lt' ? 'Atmestas' : 'Rejected'}</option>
                                                        <option value="expired">{language === 'lt' ? 'Pasibaigęs' : 'Expired'}</option>
                                                    </select>
                                                    <button
                                                        onClick={() => copyProposal(p.id)}
                                                        className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs hover:bg-indigo-200 transition-colors"
                                                        title={t.copyProposal}
                                                    >
                                                        <Copy />
                                                    </button>
                                                    <button onClick={() => loadProposal(p)} className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors">{t.load}</button>
                                                    <button onClick={() => deleteProposal(p.id)} className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 transition-colors"><Trash2 /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TEMPLATES PANEL */}
                    {showTemplates && (
                        <div className="border-t-2 pt-4 mt-4 print:hidden" style={{ borderColor: colors.primary }}>
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-lg font-semibold">{t.templates}</h3>
                                <button onClick={saveAsTemplate} className="flex items-center gap-1 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors">
                                    <Plus />
                                    {t.saveAsTemplate}
                                </button>
                            </div>
                            {templates.length === 0 ? (
                                <div className="text-center py-6 text-gray-500 border rounded-lg bg-gray-50">
                                    <p className="mb-1">{language === 'lt' ? 'Dar nėra išsaugotų šablonų' : 'No saved templates yet'}</p>
                                    <p className="text-sm">{language === 'lt' ? 'Sukurkite paslaugų paketą ir spauskite "Išsaugoti kaip šabloną"' : 'Create a service package and click "Save as Template"'}</p>
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {templates.map(template => (
                                        <div key={template.id} className="inline-flex items-center gap-2 border rounded-lg px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors group">
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-sm truncate max-w-[150px]" title={template.name}>{template.name}</div>
                                                <div className="text-xs text-gray-500">{template.sections.length} {t.sections}</div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => loadTemplate(template)}
                                                    className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
                                                    title={t.load}
                                                >
                                                    {t.load}
                                                </button>
                                                <button
                                                    onClick={() => deleteTemplate(template.id)}
                                                    className="p-1 text-red-500 hover:bg-red-100 rounded transition-colors opacity-50 group-hover:opacity-100"
                                                    title={language === 'lt' ? 'Ištrinti' : 'Delete'}
                                                >
                                                    <Trash2 />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* SECTIONS */}
                {sections.map((section) => (
                    <ProposalSection
                        key={section.id}
                        section={section}
                        colors={colors}
                        t={t}
                        showCosts={showCosts}
                        updateSectionTitle={updateSectionTitle}
                        addService={addService}
                        removeSection={removeSection}
                        updateService={updateService}
                        removeService={removeService}
                        toggleServiceExpand={toggleServiceExpand}
                        addDetail={addDetail}
                        removeDetail={removeDetail}
                        updateDetail={updateDetail}
                        addCost={addCost}
                        removeCost={removeCost}
                        updateCost={updateCost}
                        calculations={calculations}
                    />
                ))}

                <div className="text-center mb-6 print:hidden">
                    <button onClick={addSection} className="px-6 py-3 text-white rounded-lg font-semibold flex items-center gap-2 mx-auto" style={{ backgroundColor: colors.primary }}>
                        <Plus /> {t.addSection}
                    </button>
                </div>

                {/* GRAND TOTAL */}
                <div className="bg-white rounded-md shadow-sm p-4 mb-3 print:shadow-none print:mb-2">
                    <h2 className="text-sm font-normal mb-3 print:text-xs" style={{ color: colors.primary }}>{t.grandTotal}</h2>

                    <div className="space-y-1.5 border-t pt-2" style={{ borderColor: colors.primary }}>
                        <div className="flex justify-between text-sm">
                            <span style={{ color: colors.textPrimary }}>{t.servicesSubtotal}:</span>
                            <span className="font-normal" style={{ color: colors.textPrimary }}>{formatCurrency(calculateGrandTotal())}</span>
                        </div>

                        {discount.enabled && (
                            <div className="flex justify-between text-sm">
                                <span style={{ color: colors.textSecondary }}>
                                    {t.discount} ({discount.type === 'percentage' ? `${discount.value}%` : formatCurrency(discount.value)}):
                                </span>
                                <span className="font-normal text-red-600">- {formatCurrency(calculateDiscountAmount())}</span>
                            </div>
                        )}

                        <div className="flex justify-between text-sm border-t pt-1.5" style={{ borderColor: colors.textSecondary }}>
                            <span style={{ color: colors.textPrimary }}>{t.subtotalAfterDiscount}:</span>
                            <span className="font-normal" style={{ color: colors.textPrimary }}>{formatCurrency(calculateSubtotalAfterDiscount())}</span>
                        </div>

                        {vat.enabled && (
                            <div className="flex justify-between text-sm">
                                <span style={{ color: colors.textSecondary }}>PVM ({vat.rate}%):</span>
                                <span className="font-normal" style={{ color: colors.textSecondary }}>{formatCurrency(calculateVatAmount())}</span>
                            </div>
                        )}

                        {showCosts && (
                            <>
                                <div className="flex justify-between items-center text-lg pb-4 border-b print:hidden">
                                    <span className="font-semibold" style={{ color: colors.cost }}>{t.totalCost}</span>
                                    <span className="font-bold" style={{ color: colors.cost }}>{formatCurrency(calculateGrandCost())}</span>
                                </div>
                                <div className="flex justify-between items-center text-2xl pt-2 print:hidden">
                                    <span className="font-bold" style={{ color: colors.success }}>{t.margin}</span>
                                    <span className="font-bold" style={{ color: colors.success }}>
                                        {formatCurrency(calculateGrandMargin())} ({calculateGrandMarginPercent()}%)
                                    </span>
                                </div>
                            </>
                        )}

                        <div className="flex justify-between text-sm border-t-2 pt-2" style={{ borderColor: colors.primary }}>
                            <span className="font-normal" style={{ color: colors.textPrimary }}>{language === 'lt' ? 'Bendra suma su PVM' : 'Total incl. VAT'}:</span>
                            <span className="font-normal" style={{ color: colors.primary }}>{formatCurrency(calculateFinalTotal())}</span>
                        </div>
                    </div>

                    {/* PAYMENT SCHEDULE */}
                    {paymentTerms.enabled && (
                        <div className="mt-8 pt-8 border-t-2">
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>{t.paymentSchedule}</h3>
                            <div className="space-y-2">
                                {paymentTerms.stages.map((stage) => (
                                    <div key={stage.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                        <div>
                                            <div className="font-semibold">{stage.name}</div>
                                            <div className="text-sm text-gray-600">{stage.description}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-bold text-lg" style={{ color: colors.primary }}>
                                                {formatCurrency(calculateFinalTotal() * stage.percentage / 100)}
                                            </div>
                                            <div className="text-sm text-gray-600">{stage.percentage}%</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* COMPANY INFO FOOTER */}
                    <div className="mt-8 pt-8 border-t-2 grid grid-cols-2 gap-8">
                        <div>
                            <h3 className="font-bold mb-2" style={{ color: colors.primary }}>{companyInfo.name}</h3>
                            <div className="text-sm space-y-1" style={{ color: colors.textSecondary }}>
                                <div>{t.companyCode}: {companyInfo.companyCode}</div>
                                <div>{t.vatCode}: {companyInfo.vatCode}</div>
                                <div>{companyInfo.address}</div>
                                <div>{companyInfo.phone}</div>
                                <div>{companyInfo.email}</div>
                                <div>{companyInfo.website}</div>
                            </div>
                        </div>
                        <div>
                            <h3 className="font-bold mb-2" style={{ color: colors.primary }}>{t.bankDetails}</h3>
                            <div className="text-sm space-y-1" style={{ color: colors.textSecondary }}>
                                <div>{t.bankAccount}: {companyInfo.bankAccount}</div>
                                <div>{t.bankName}: {companyInfo.bankName}</div>
                            </div>
                        </div>
                    </div>

                    {/* TERMS */}
                    <div className="mt-8 pt-8 border-t-2">
                        <h3 className="font-bold mb-3" style={{ color: colors.primary }}>{t.termsAndConditions}</h3>
                        <div className="text-sm space-y-2" style={{ color: colors.textSecondary }}>
                            {terms.payment && <p>{terms.payment}</p>}
                            {terms.validity && <p>{terms.validity}</p>}
                            {terms.additional && <p>{terms.additional}</p>}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
